<template>
    <div class="object-block-contact" :class="objectBlockClasses">
        <template v-if="variant === OBJECT_BLOCK_VARIANT.PREVIEW && state === ENUMS_STATE.EMPTY">
            <CompassObject object="contact" :size="objectSize" />
            <div class="typographies-paragraph-medium-regular object-block__header-caption">
                {{ $t.__('overview.contactBlock.empty.description') }}
            </div>
            <CompassButton
                size="small"
                type="secondary"
                :label="$t.__('overview.contactBlock.empty.button')"
                @click="$emit('handleCreateContact')"
            />
        </template>
        <template v-else-if="variant === OBJECT_BLOCK_VARIANT.SELECTION">
            <template v-if="state === ENUMS_STATE.DEFAULT">
                <div class="object-block__header-title-container">
                    <BlockName :name="fullName" :link="linkCard" />
                    <span
                        class="typographies-paragraph-medium-regular object-block__header-caption"
                    >
                        {{ $filters.capitalize(objectCaption) }}
                    </span>
                </div>
                <CompassButton
                    leftIcon="plus"
                    type="tertiary"
                    :label="$t.__('common.actions.contact_link_to', { object: 'idk' })"
                    @click="$emit('handleLinkToObject')"
                />
            </template>
            <template v-else-if="state === ENUMS_STATE.SELECTED">
                <div class="object-block__header-title-container">
                    <span
                        class="object-block-contact__header-name typographies-paragraph-medium-semibold"
                    >
                        <CompassObject object="avatar" :size="objectSize" />
                        <BlockName :name="fullName" :link="linkCard" />
                    </span>
                    <span
                        class="typographies-paragraph-medium-regular object-block__header-caption"
                    >
                        {{ $filters.capitalize(objectCaption) }}
                    </span>
                </div>
                <div class="object-block__header-actions">
                    <CompassButton
                        type="tertiary"
                        :label="$t.__('common.actions.edit')"
                        @click="$emit('edit')"
                    />
                    <CompassButton
                        type="tertiary"
                        :label="$t.__('common.actions.unlink_object')"
                        @click="$emit('unlink')"
                    />
                </div>
            </template>
            <template v-else>
                <CompassObject object="avatar" :size="objectSize" />
                <span class="typographies-paragraph-medium-regular object-block__header-caption">
                    {{ $t.__('common.object.BlockContact.labels.no_contact_linked') }}
                </span>
            </template>
        </template>
        <template v-else>
            <div class="object-block__header">
                <div class="object-block__header-title-container">
                    <CompassObject object="contact" :size="objectSize" />
                    <div>
                        <BlockName :name="fullName" :link="!displayLink ? linkCard : null" />
                        <div
                            class="typographies-paragraph-small-regular object-block__header-caption"
                        >
                            {{ $filters.capitalize(objectCaption) }}
                        </div>
                    </div>
                </div>
                <CompassLink
                    v-if="displayLink"
                    :label="linkLabel"
                    :link="linkCard"
                    target="_blank"
                />
                <CompassIconButton
                    v-if="variant === OBJECT_BLOCK_VARIANT.LINKED"
                    icon="close"
                    @click="$emit('unlink')"
                />
            </div>
            <BlockDescriptionListPart
                v-if="displayDescriptionList && hasDescriptionListItem"
                v-bind="$props"
                :showCompany="displayCompanyDescriptionItem"
            />
            <slot v-if="variant === OBJECT_BLOCK_VARIANT.OVERVIEW" name="company" />
        </template>
    </div>
</template>

<script>
import BlockGeneric from 'vcomponents/common/object/BlockGeneric.vue';
import BlockDescriptionListPart from 'vcomponents/common/object/BlockDescriptionListPart.vue';
import { getFullNameUser } from 'Helpers/avatarHelper.js';

const ENUMS_STATE = Object.freeze({
    DEFAULT: 'default',
    EMPTY: 'empty',
    SELECTED: 'selected',
});

export default {
    components: { BlockDescriptionListPart },
    extends: BlockGeneric,
    props: {
        state: {
            type: String,
            default: ENUMS_STATE.DEFAULT,
            validator: (value) => {
                return Object.values(ENUMS_STATE).includes(value);
            },
        },
        isEmbedded: {
            type: Boolean,
            default: false,
        },
        showCompany: {
            type: Boolean,
            default: true,
        },
        showEmail: {
            type: Boolean,
            default: true,
        },
        showPhone: {
            type: Boolean,
            default: true,
        },
        showMobile: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            ENUMS_STATE,
            fullName: '',
        };
    },
    computed: {
        displayCompanyDescriptionItem() {
            return this.showCompany && !!this.data.company && !this.hasSlotCompany;
        },
        hasDescriptionListItem() {
            return (
                this.displayCompanyDescriptionItem ||
                (this.showEmail && !!this.data.email) ||
                (this.showPhone && !!this.data.phone_number) ||
                (this.showMobile && !!this.data.mobile_number)
            );
        },
        objectBlockClasses() {
            return [
                'object-block',
                `object-block--${this.variant}`,
                `object-block--${this.state}`,
                this.isEmbedded ? 'object-block--embedded' : '',
            ];
        },
        objectSize() {
            return this.isEmbedded ? 'extra-small' : 'small';
        },
    },
    created() {
        this.linkCard = this.getLink({
            id: this.getObjectId(),
            type: 'people',
            subtype: this.data.object ? this.data.object.sub_type : this.data.type,
        });
        this.objectCaption = this.data.position;
        this.fullName = getFullNameUser(this.data, true);
    },
};
</script>
<style lang="scss" scoped>
.object-block-contact {
    &.object-block--selection {
        &:not(.object-block--empty) {
            align-items: center;
            display: flex;
            gap: var(--spacing-4);
            justify-content: space-between;

            .object-block__header-title-container {
                align-items: center;
                display: flex;
                gap: var(--spacing-3);

                & > div:has(.compass-object) {
                    align-items: center;
                    display: flex;
                    gap: var(--spacing-1);
                }
            }

            .compass-button {
                flex-shrink: 0;
            }
        }

        &.object-block--empty {
            align-items: center;
            display: flex;
            flex-direction: column;
            gap: var(--spacing-1);
        }
    }

    &.object-block--preview.object-block--empty {
        align-items: center;
        display: flex;
        flex-direction: column;
        gap: var(--spacing-1);
    }

    .object-block-company {
        margin-top: var(--spacing-3);
    }
    &__header-name {
        align-items: center;
        display: flex;
        gap: var(--spacing-1);
    }
}
</style>
